import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Cancel,
  Pending,
  Stop,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { apiService } from '../services/api';

interface TemplateSendProgressProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  totalRecords: number;
  templateName: string;
  onComplete?: () => void;
}

interface ProgressUpdate {
  recordId: string;
  status: string;
  recipientNumber: string;
  errorMessage?: string;
  messageId?: string;
}

const TemplateSendProgress: React.FC<TemplateSendProgressProps> = ({
  open,
  onClose,
  sessionId,
  totalRecords,
  templateName,
  onComplete,
}) => {
  const { socket } = useSocket();
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [recentUpdates, setRecentUpdates] = useState<ProgressUpdate[]>([]);
  const [sessionStatus, setSessionStatus] = useState<string>('in_progress');
  const [isCancelling, setIsCancelling] = useState(false);

  const isComplete = sessionStatus === 'completed' || sessionStatus === 'cancelled';
  const processedCount = successCount + failedCount + cancelledCount;
  const pendingCount = Math.max(0, totalRecords - processedCount);
  const progress = totalRecords > 0 ? (processedCount / totalRecords) * 100 : 0;

  useEffect(() => {
    setSuccessCount(0);
    setFailedCount(0);
    setCancelledCount(0);
    setRecentUpdates([]);
    setSessionStatus('in_progress');
    setIsCancelling(false);
  }, [sessionId]);

  useEffect(() => {
    if (!open || !sessionId) return;

    const loadSession = async () => {
      try {
        const response = await apiService.getTemplateSendSessionDetails(sessionId, {
          page: 1,
          limit: 1,
        });
        const session = response.data.data?.session;
        if (session) {
          setSessionStatus(session.status);
          setSuccessCount(session.successCount || 0);
          setFailedCount(session.failedCount || 0);
          setCancelledCount(session.cancelledCount || 0);
        }
      } catch (error) {
        console.error('Failed to load session details:', error);
      }
    };

    loadSession();
  }, [open, sessionId]);

  useEffect(() => {
    if (!socket || !open) return;

    const handleProgress = (data: any) => {
      if (data.sessionId !== sessionId) return;

      if (data.status === 'success') {
        setSuccessCount(prev => prev + 1);
      } else if (data.status === 'failed') {
        setFailedCount(prev => prev + 1);
      }

      setRecentUpdates(prev => [
        {
          recordId: data.recordId,
          status: data.status,
          recipientNumber: data.recipientNumber,
          errorMessage: data.errorMessage,
          messageId: data.messageId,
        },
        ...prev.slice(0, 9),
      ]);
    };

    const handleComplete = (data: any) => {
      if (data.sessionId !== sessionId) return;

      setSessionStatus('completed');
      setSuccessCount(data.successCount);
      setFailedCount(data.failedCount);
      setCancelledCount(data.cancelledCount);
      onComplete?.();
    };

    const handleCancelled = (data: any) => {
      if (data.sessionId !== sessionId) return;

      setSessionStatus('cancelled');
      setIsCancelling(false);
      setCancelledCount(data.cancelledCount ?? cancelledCount);
      onComplete?.();
    };

    socket.on('template_send_progress', handleProgress);
    socket.on('template_send_complete', handleComplete);
    socket.on('template_send_cancelled', handleCancelled);

    return () => {
      socket.off('template_send_progress', handleProgress);
      socket.off('template_send_complete', handleComplete);
      socket.off('template_send_cancelled', handleCancelled);
    };
  }, [socket, open, sessionId, cancelledCount, onComplete]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this sending session?')) {
      return;
    }

    setIsCancelling(true);
    try {
      await apiService.cancelWhatsAppTemplateSend(sessionId);
    } catch (error: any) {
      console.error('Error cancelling session:', error);
      alert('Failed to cancel session. Please try again.');
      setIsCancelling(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" fontSize="small" />;
      case 'failed':
        return <Error color="error" fontSize="small" />;
      case 'cancelled':
        return <Cancel color="disabled" fontSize="small" />;
      default:
        return <Pending color="info" fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'info';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Sending: {templateName}
          </Typography>
          {!isComplete && (
            <Tooltip title="Cancel sending">
              <IconButton
                onClick={handleCancel}
                disabled={isCancelling}
                color="error"
                size="small"
              >
                <Stop />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              <Chip
                label={`Status: ${sessionStatus}`}
                color={isComplete ? 'default' : 'primary'}
                size="small"
              />
              {pendingCount > 0 && (
                <Chip label={`Remaining: ${pendingCount}`} size="small" variant="outlined" />
              )}
            </Box>

            <Typography variant="subtitle2" gutterBottom>
              Progress: {Math.round(progress)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ mb: 2, height: 8, borderRadius: 1 }}
            />

            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                icon={<Pending />}
                label={`Pending: ${pendingCount}`}
                color="info"
                variant="outlined"
              />
              <Chip
                icon={<CheckCircle />}
                label={`Success: ${successCount}`}
                color="success"
                variant={successCount > 0 ? 'filled' : 'outlined'}
              />
              <Chip
                icon={<Error />}
                label={`Failed: ${failedCount}`}
                color="error"
                variant={failedCount > 0 ? 'filled' : 'outlined'}
              />
              {cancelledCount > 0 && (
                <Chip
                  icon={<Cancel />}
                  label={`Cancelled: ${cancelledCount}`}
                  color="default"
                  variant="filled"
                />
              )}
            </Box>
          </Paper>

          {isComplete && (
            <Alert severity={failedCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {sessionStatus === 'cancelled'
                  ? `Sending cancelled. ${successCount} sent successfully, ${failedCount} failed, ${cancelledCount} cancelled.`
                  : `Sending complete! ${successCount} sent successfully${failedCount > 0 ? `, ${failedCount} failed` : ''}.`}
              </Typography>
            </Alert>
          )}

          {!isComplete && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can minimize this dialog; sending continues in the background and stays visible in the active sends bar.
            </Alert>
          )}

          {recentUpdates.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Recent Activity
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="60px">Status</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentUpdates.map((update, index) => (
                      <TableRow key={`${update.recordId}-${index}`}>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(update.status)}
                            label={update.status}
                            color={getStatusColor(update.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {update.recipientNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {update.status === 'success' && update.messageId && (
                            <Typography variant="caption" color="text.secondary">
                              Message ID: {update.messageId.substring(0, 20)}...
                            </Typography>
                          )}
                          {update.status === 'failed' && update.errorMessage && (
                            <Tooltip title={update.errorMessage}>
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  display: 'block',
                                  maxWidth: '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {update.errorMessage}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isComplete ? (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>
              Minimize
            </Button>
            <Button onClick={handleCancel} color="error" disabled={isCancelling}>
              {isCancelling ? 'Cancelling...' : 'Cancel Sending'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSendProgress;
